import React, { useEffect, useRef, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Auto-scrolling, infinitely looping carousel for boosted listings.
 * - Continuous smooth left→right motion (content drifts to the right).
 * - Pauses on hover, resumes after.
 * - Manual drag (pointer / touch); a real drag swallows the click.
 * - Clickable prev/next arrows and navigation dots.
 * - Respects prefers-reduced-motion.
 *
 * A float `pos` is the source of truth because browsers round `scrollLeft`
 * to integers — a sub-pixel per-frame speed would otherwise never advance.
 */
export function BoostedCarousel({ items, renderItem, keyFor, itemClassName = "w-80", speed = 0.95, testid = "boosted-carousel" }) {
  const { t } = useLang();
  const ref = useRef(null);
  const s = useRef({ paused: false, dragging: false, moved: false, startX: 0, startPos: 0, raf: 0, oneCopy: 1, itemW: 1, pos: 0, target: null });
  const [active, setActive] = useState(0);

  const n = items.length;
  // Enough duplicated copies that the strip is always wider than
  // (2 * oneCopy + viewport). Otherwise blank space appears after the last
  // card before the loop wraps, which reads as a "pause" between cycles.
  const copies = Math.max(4, Math.ceil(8 / Math.max(n, 1)) + 2);
  const loop = useMemo(() => Array.from({ length: copies }).flatMap(() => items), [items, copies]);

  useEffect(() => {
    const el = ref.current;
    if (!el || n === 0) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const measure = () => {
      s.current.oneCopy = Math.max(1, el.scrollWidth / copies);
      s.current.itemW = s.current.oneCopy / n;
      s.current.pos = s.current.oneCopy;
      el.scrollLeft = s.current.pos;
    };
    measure();
    window.addEventListener("resize", measure);

    const wrap = () => {
      const one = s.current.oneCopy;
      if (s.current.pos < one) {
        s.current.pos += one;
        if (s.current.target != null) s.current.target += one;
      } else if (s.current.pos >= 2 * one) {
        s.current.pos -= one;
        if (s.current.target != null) s.current.target -= one;
      }
    };

    let lastIdx = -1;
    const step = () => {
      const st = s.current;
      if (st.target != null) {
        const diff = st.target - st.pos;
        if (Math.abs(diff) < 0.6) { st.pos = st.target; st.target = null; }
        else st.pos += diff * 0.18; // ease toward target
        wrap(); el.scrollLeft = st.pos;
      } else if (!reduce && !st.paused && !st.dragging && !st.manual) {
        st.pos -= speed; wrap(); el.scrollLeft = st.pos;
      }
      const idx = ((Math.round((st.pos - st.oneCopy) / st.itemW) % n) + n) % n;
      if (idx !== lastIdx) { lastIdx = idx; setActive(idx); }
      st.raf = requestAnimationFrame(step);
    };
    s.current.raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(s.current.raf); window.removeEventListener("resize", measure); };
  }, [n, copies, speed]);

  const onPointerDown = (e) => {
    const st = s.current;
    st.dragging = true; st.moved = false; st.captured = false; st.pointerId = e.pointerId; st.target = null;
    st.startX = e.clientX; st.startPos = st.pos;
    // NOTE: do NOT setPointerCapture here — on desktop that retargets the
    // subsequent click to this container and swallows clicks on child <Link>s.
    // We capture only once a real drag begins (see onPointerMove).
  };
  const onPointerMove = (e) => {
    const st = s.current;
    if (!st.dragging) return;
    const el = ref.current;
    const dx = e.clientX - st.startX;
    if (Math.abs(dx) > 4) {
      st.moved = true;
      if (!st.captured) { try { el.setPointerCapture(st.pointerId); st.captured = true; } catch { /* ignore */ } }
    }
    if (!st.moved) return; // a stationary press must stay a click, not a drag
    const one = st.oneCopy;
    let p = st.startPos - dx;
    if (p < one) { p += one; st.startX = e.clientX; st.startPos = p + dx; }
    else if (p >= 2 * one) { p -= one; st.startX = e.clientX; st.startPos = p + dx; }
    st.pos = p; el.scrollLeft = p;
  };
  const endDrag = () => {
    const st = s.current;
    if (st.captured) { try { ref.current.releasePointerCapture(st.pointerId); } catch { /* ignore */ } st.captured = false; }
    st.dragging = false;
  };
  const onClickCapture = (e) => {
    if (s.current.moved) { e.preventDefault(); e.stopPropagation(); s.current.moved = false; }
  };

  const nudge = (dir) => {
    const st = s.current;
    st.manual = true; // stop auto-scroll: step one item per click, then wait
    const base = st.target != null ? st.target : st.pos;
    let t = base + dir * st.itemW;
    // keep target inside the wrap window [oneCopy, 2*oneCopy] like goTo(),
    // otherwise wrap() re-centres st.pos every frame and the ease never settles.
    while (t - st.pos > st.oneCopy / 2) t -= st.oneCopy;
    while (st.pos - t > st.oneCopy / 2) t += st.oneCopy;
    st.target = t;
  };
  const goTo = (i) => {
    const st = s.current;
    st.manual = true;
    let t = st.oneCopy + i * st.itemW;
    // choose nearest equivalent copy to avoid a long sweep
    while (t - st.pos > st.oneCopy / 2) t -= st.oneCopy;
    while (st.pos - t > st.oneCopy / 2) t += st.oneCopy;
    st.target = t;
  };

  if (!n) return null;

  return (
    <div className="relative group" data-testid={`${testid}-wrap`}>
      <div
        ref={ref}
        data-testid={testid}
        className="flex gap-5 overflow-x-hidden select-none cursor-grab active:cursor-grabbing py-1 -mx-1 px-1"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        onMouseEnter={() => (s.current.paused = true)}
        onMouseLeave={() => (s.current.paused = false)}
        style={{ scrollbarWidth: "none", contain: "layout paint style", transform: "translateZ(0)", backfaceVisibility: "hidden", WebkitOverflowScrolling: "touch" }}
      >
        {loop.map((it, i) => (
          <div key={`${keyFor ? keyFor(it) : i}-${i}`} className={`shrink-0 ${itemClassName}`} data-testid="boosted-carousel-item">
            {renderItem(it)}
          </div>
        ))}
      </div>

      {n > 1 && (
        <>
          <button onClick={() => nudge(-1)} aria-label={t("hc.previous")} data-testid={`${testid}-prev`}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-20 h-10 w-10 grid place-items-center rounded-full bg-white/95 text-findo-navy shadow-lg border border-border hover:bg-white transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100">
            <ChevronLeft size={20} />
          </button>
          <button onClick={() => nudge(1)} aria-label={t("hc.next")} data-testid={`${testid}-next`}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 h-10 w-10 grid place-items-center rounded-full bg-white/95 text-findo-navy shadow-lg border border-border hover:bg-white transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100">
            <ChevronRight size={20} />
          </button>
        </>
      )}
    </div>
  );
}
